import type { ApiError } from '@repo/sdk/gen'
import { Button } from '@repo/ui/button'
import {
	Empty,
	EmptyContent,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia
} from '@repo/ui/empty'
import { Skeleton } from '@repo/ui/skeleton'
import { getErrorCode } from '@repo/ui/utils'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
	Clock,
	CloudOff,
	Link2Off,
	type LucideIcon,
	Timer,
	UserCheck
} from 'lucide-react'
import { type ReactNode, useEffect } from 'react'
import {
	changeAppLanguage,
	isAppLanguageCode,
	useTranslation
} from '@/lib/i18n'
import { warmInstanceBranding } from '@/query/query-options'
import { ActivationForm } from './-components/activation-form'
import { operatorInvitationQueryOptions } from './-query/query-options'

/**
 * The page an operator invitation email links to. The URL is the backend's to
 * build (`frontend.admin.authPaths.acceptInvite` plus the code), so this path
 * is fixed by the emails already sent.
 *
 * Public, beside `/login` and outside the `(private)` gate: the person has no
 * account yet. `ssr: false`: the code should not travel through a server
 * render, and nothing here is worth a server-rendered first response.
 */
export const Route = createFileRoute('/accept-invite/$code/')({
	ssr: false,
	loader: ({ context }) => warmInstanceBranding(context.queryClient),
	component: AcceptInvitePage
})

function AcceptInvitePage() {
	const { code } = Route.useParams()
	const { t } = useTranslation()
	const invitation = useQuery(operatorInvitationQueryOptions(code))

	const language = invitation.data?.language

	useEffect(() => {
		// The invitation was written in one language, and this is the first
		// page its recipient sees.
		if (isAppLanguageCode(language)) {
			changeAppLanguage(language)
		}
	}, [language])

	return (
		<AcceptInviteFrame>
			{invitation.isLoading && <InvitationLoading />}
			{invitation.isError && (
				<InvitationRefused error={invitation.error} />
			)}
			{invitation.data && (
				<>
					<h1 className='font-semibold text-2xl tracking-tight'>
						{t('operator-accept-invite:title')}
					</h1>
					<p className='mt-2 text-muted-foreground text-sm'>
						{t('operator-accept-invite:description')}
					</p>
					<div className='mt-6'>
						<ActivationForm
							code={code}
							invitation={invitation.data}
						/>
					</div>
				</>
			)}
		</AcceptInviteFrame>
	)
}

/**
 * The client's invitation page frame (`apps/client/src/routes/invite/$code`):
 * one plain 400px column, centred, no card. The structure only — the console
 * draws none of the instance's brand.
 */
function AcceptInviteFrame({ children }: { children: ReactNode }) {
	return (
		<div className='flex min-h-full w-full flex-col items-center justify-center px-4 py-12'>
			<main className='flex w-full max-w-[400px] flex-col'>
				{children}
			</main>
		</div>
	)
}

function InvitationLoading() {
	const { t } = useTranslation()

	return (
		<output
			aria-label={t('operator-accept-invite:loading')}
			className='flex flex-col gap-4'
		>
			<Skeleton className='h-8 w-2/3' />
			<Skeleton className='h-4 w-full' />
			<Skeleton className='mt-2 h-9 w-full' />
		</output>
	)
}

/**
 * Why the link does not work, in words that say what to do next. «Expired»
 * and «already used» point at different next steps — ask for a new invitation,
 * or just sign in — and only the second is one this page can take.
 */
function InvitationRefused({ error }: { error: unknown }) {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const code = getErrorCode(error)
	const reason =
		code !== undefined && code in REFUSAL_KEYS
			? REFUSAL_KEYS[code as keyof typeof REFUSAL_KEYS]
			: 'unavailable'
	const Icon = REFUSAL_ICONS[reason]

	return (
		<Empty className='p-0'>
			<EmptyHeader>
				<EmptyMedia variant='icon'>
					<Icon aria-hidden />
				</EmptyMedia>
				<h1 className='font-semibold text-xl tracking-tight'>
					{t(`operator-accept-invite:refused.${reason}.title`)}
				</h1>
				<EmptyDescription>
					{t(`operator-accept-invite:refused.${reason}.description`)}
				</EmptyDescription>
			</EmptyHeader>
			{reason === 'already_used' && (
				<EmptyContent>
					<Button onClick={() => navigate({ to: '/login' })}>
						{t(
							'operator-accept-invite:refused.already_used.go_to_login'
						)}
					</Button>
				</EmptyContent>
			)}
		</Empty>
	)
}

const REFUSAL_KEYS = {
	invite_code_invalid: 'invalid',
	invite_expired: 'expired',
	invite_already_used: 'already_used',
	rate_limited: 'rate_limited'
} as const satisfies Partial<Record<ApiError['code'], string>>

const REFUSAL_ICONS = {
	invalid: Link2Off,
	expired: Clock,
	already_used: UserCheck,
	rate_limited: Timer,
	unavailable: CloudOff
} satisfies Record<
	(typeof REFUSAL_KEYS)[keyof typeof REFUSAL_KEYS] | 'unavailable',
	LucideIcon
>
